import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import TribeLogo, { TribeMark } from "@/components/TribeLogo";


function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = { email: email.trim().toLowerCase(), password };
      if (mfaRequired || mfaCode.trim()) payload.mfa_code = mfaCode.trim();
      const { data } = await axios.post(`${API}/auth/login`, payload, { withCredentials: true });
      if (data.mfa_required && !data.access_token) {
        setMfaRequired(true);
        setError("");
        return;
      }
      login(data.access_token, data.user);
      if (data.refresh_token) localStorage.setItem("loyalty_refresh", data.refresh_token);
      const role = data.user?.role;
      if (role === "staff") navigate("/scan");
      else if (role === "vendor" && !data.user?.vendor_id) navigate("/onboarding");
      else navigate("/dashboard");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex font-sans bg-surface">
      {/* Left panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2.5 mb-10">
            <TribeMark size={40} />
            <div>
              <TribeLogo size="sm" />
              <p className="text-xs text-quiet mt-0.5">Superadmin Console</p>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold text-ink tracking-tight">Sign in</h2>
            <p className="text-sm text-ink/50 mt-1.5">Access your rewards management dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold text-ink/60 uppercase tracking-widest">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@loyaltyengine.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="h-11 rounded-xl border-ink/15 bg-white focus:border-purple-300 focus:ring-purple-300"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold text-ink/60 uppercase tracking-widest">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="h-11 pr-10 rounded-xl border-ink/15 bg-white focus:border-purple-300 focus:ring-purple-300"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/30 hover:text-ink/60"
                  data-testid="toggle-password-visibility"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {mfaRequired && (
              <div className="space-y-1.5">
                <Label htmlFor="mfa" className="text-xs font-semibold text-ink/60 uppercase tracking-widest">
                  Authenticator code
                </Label>
                <Input
                  id="mfa"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="6-digit code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  required
                  data-testid="login-mfa-input"
                  className="h-11 rounded-xl border-ink/15 bg-white tracking-widest"
                />
              </div>
            )}

            {error && (
              <div className="px-4 py-2.5 rounded-xl bg-purple-50 border border-danger/30 text-danger text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 rounded-full bg-purple-500 hover:bg-purple-600 text-white font-semibold text-sm"
            >
              {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <p className="mt-8 text-xs text-ink/30 text-center">
            Tribe &copy; {new Date().getFullYear()}
          </p>

          <p className="mt-3 text-sm text-ink/50 text-center">
            Vendor?{" "}
            <a href="/register" className="text-ink font-semibold hover:text-purple-700" data-testid="go-to-register-link">Register here</a>
          </p>
        </div>
      </div>

      {/* Right panel */}
      <div className="hidden lg:flex flex-1 bg-purple-500 items-center justify-center px-12">
        <div className="max-w-md text-center">
          <TribeLogo variant="purple" size="lg" className="justify-center mb-8 text-4xl" />
          <h3 className="text-3xl font-display font-bold text-white mb-4 tracking-tight">Multi-Tenant Rewards Platform</h3>
          <p className="text-white/80 text-base leading-relaxed font-body">
            Manage clients, track rewards, and drive customer engagement — all from one powerful dashboard.
          </p>
          <div className="flex items-center justify-center gap-3 mt-8">
            <span className="px-4 py-1.5 rounded-pill bg-white/15 text-white text-xs font-medium">Apple Wallet</span>
            <span className="px-4 py-1.5 rounded-pill bg-white/15 text-white text-xs font-medium">Google Wallet</span>
            <span className="px-4 py-1.5 rounded-pill bg-white/15 text-white text-xs font-medium">Push Notifications</span>
          </div>
        </div>
      </div>
    </div>
  );
}
